import { useEffect, useState } from 'react';
import type { AxiosInstance } from 'axios';
import {
  LdsAlert, LdsBox, LdsButton, LdsCheckbox, LdsChip, LdsDialog,
  LdsFormField, LdsIconButton, LdsMenu, LdsMenuItem, LdsProgressCircular,
  LdsTable, LdsTextField, LdsTypography,
} from '@lds/react';
import { LdsIconPlus, LdsIconPencil, LdsIconTrash, LdsIconRefresh, LdsIconChevronLeft, LdsIconChevronRight, LdsIconBookmark } from '@lds/react-icons';
import type { ManagedEnvironment } from '../../api/types';
import { useAlbWafController } from './AlbWafPage.controller';
import styles from './AlbWafPage.module.scss';

interface AlbWafPageProps {
  apiClient: AxiosInstance;
  managedEnvironment: ManagedEnvironment;
  embedded?: boolean;
}

export function AlbWafPage({ apiClient, managedEnvironment, embedded = false }: AlbWafPageProps) {
  const controller = useAlbWafController(apiClient, managedEnvironment);
  const [confirmation, setConfirmation] = useState<'save' | 'reload' | number | null>(null);
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const [defaultMenu, setDefaultMenu] = useState<{ anchor: HTMLElement; value: string } | null>(null);
  const draft = controller.draft;
  const filtered = draft?.catalog.map((entry, index) => ({ entry, index })).filter(({ entry }) =>
    `${entry.value} ${entry.description}`.toLowerCase().includes(search.toLowerCase())) ?? [];
  const lastPage = Math.max(1, Math.ceil(filtered.length / 10));
  const currentPage = Math.min(page, lastPage);
  const rows = filtered.slice((currentPage - 1) * 10, currentPage * 10);
  const editorInUse = Boolean(draft && controller.editor && controller.editor.index !== null
    && [draft.public_default, draft.private_default].includes(draft.catalog[controller.editor.index]?.value));

  useEffect(() => { setDefaultMenu(null); }, [apiClient, managedEnvironment]);

  const closeDefaultMenu = () => {
    defaultMenu?.anchor.focus({ preventScroll: true });
    setDefaultMenu(null);
  };

  const selectDefault = (field: 'public_default' | 'private_default') => {
    if (defaultMenu) controller.changeDefault(field, defaultMenu.value);
    closeDefaultMenu();
  };

  const confirm = () => {
    if (confirmation === 'save') void controller.save();
    else if (confirmation === 'reload') controller.reload();
    else if (typeof confirmation === 'number') controller.removeEntry(confirmation);
    setConfirmation(null);
  };

  return (
    <LdsBox className={styles.page} data-testid="alb-waf-page">
      {!embedded && <LdsBox className={styles.header}>
        <LdsBox>
          <LdsTypography variant="headingXl">ALB / WAF</LdsTypography>
          <LdsBox className={styles.identity}>
            <LdsTypography variant="bodySm">waf-policy</LdsTypography>
            <LdsChip label={managedEnvironment.toUpperCase()} color="neutral" size="sm" />
            {controller.role && <LdsChip label={controller.role === 'admin' ? 'Admin' : 'Viewer'} color="neutral" size="sm" />}
          </LdsBox>
        </LdsBox>
      </LdsBox>}

      {controller.loading && <LdsBox className={styles.loading}>
        <LdsProgressCircular indeterminate animated ariaLabel="Carregando ALB WAF" />
      </LdsBox>}
      {controller.error && <LdsBox className={styles.failure}>
        <LdsBox className={styles.failureMessage}>
          <LdsAlert color="critical" description={controller.error} data-testid="alb-waf-error" />
        </LdsBox>
        <LdsIconButton size="sm" ariaLabel="Tentar novamente" title="Tentar novamente"
          disabled={controller.loading || controller.saving}
          onClick={() => controller.dirty ? setConfirmation('reload') : controller.reload()}>
          <LdsIconRefresh size="16" />
        </LdsIconButton>
      </LdsBox>}
      {controller.configurationMissing && controller.role === 'viewer'
        && <LdsAlert color="info" description={'Configura\u00e7\u00e3o inicial ainda n\u00e3o criada. Solicite a inicializa\u00e7\u00e3o a um Admin.'} />}
      {controller.success && <LdsAlert color="success" description={'Configura\u00e7\u00e3o salva.'} />}
      {draft && !controller.loading && <>
        {controller.configurationMissing && <LdsAlert color="info" description={'Configura\u00e7\u00e3o inicial ainda n\u00e3o criada. Nenhum valor foi pr\u00e9-aprovado; informe manualmente o cat\u00e1logo e os dois padr\u00f5es.'} />}
        <LdsBox className={styles.band}>
          <LdsBox className={styles.toolbar}>
            <LdsBox className={styles.search}>
              <LdsFormField label="Buscar policy" fieldId="alb-search">
                <LdsTextField id="alb-search" aria-label="Buscar policy" value={search}
                  onChange={(event) => { setSearch(event.target.value); setPage(1); }} />
              </LdsFormField>
            </LdsBox>
            {controller.role === 'admin' && <LdsButton variant="contained" size="sm" disabled={!controller.canEdit}
              aria-label="Adicionar policy" onClick={() => controller.openEditor(null)}>
              <LdsBox className={styles.inline}><LdsIconPlus size="14" /> Adicionar policy</LdsBox>
            </LdsButton>}
          </LdsBox>
          <LdsBox className={styles.tableScroll}>
            <LdsTable aria-label="Catalogo ALB WAF" striped>
              <thead><tr><th>Policy</th><th>{'Descri\u00e7\u00e3o'}</th><th>Status</th>{controller.role === 'admin' && <th>{'A\u00e7\u00f5es'}</th>}</tr></thead>
              <tbody>{rows.map(({ entry, index }) => {
                const inUse = [draft.public_default, draft.private_default].includes(entry.value);
                return <tr key={entry.value}>
                  <td className={styles.policyValue}><LdsBox className={styles.policyIdentity}>
                    <LdsTypography variant="bodyMd">{entry.value}</LdsTypography>
                    {entry.value === draft.public_default && <LdsChip label={'Padr\u00e3o p\u00fablico'} color="critical" size="sm" />}
                    {entry.value === draft.private_default && <LdsChip label={'Padr\u00e3o privado'} color="info" size="sm" />}
                  </LdsBox></td><td>{entry.description || '-'}</td>
                  <td><LdsChip label={entry.approved ? 'Aprovada' : 'Pendente'} color={entry.approved ? 'success' : 'neutral'} size="sm" /></td>
                  {controller.role === 'admin' && <td><LdsBox className={styles.inline}>
                    <LdsIconButton size="sm" ariaLabel={`Definir padr\u00f5es para ${entry.value}`}
                      title={entry.approved ? 'Definir padr\u00f5es' : 'A policy precisa estar aprovada'}
                      aria-haspopup="menu" aria-expanded={defaultMenu?.value === entry.value}
                      disabled={!controller.canEdit || !entry.approved}
                      onClick={(event) => {
                        if (event.currentTarget instanceof HTMLElement) {
                          setDefaultMenu({ anchor: event.currentTarget, value: entry.value });
                        }
                      }}>
                      <LdsIconBookmark size="16" />
                    </LdsIconButton>
                    <LdsIconButton size="sm" ariaLabel={`Editar ${entry.value}`} title={`Editar ${entry.value}`}
                      disabled={!controller.canEdit} onClick={() => controller.openEditor(index)}><LdsIconPencil size="16" /></LdsIconButton>
                    <LdsIconButton size="sm" ariaLabel={`Remover ${entry.value}`} title={inUse ? 'Default em uso' : `Remover ${entry.value}`}
                      disabled={!controller.canEdit || inUse} onClick={() => setConfirmation(index)}><LdsIconTrash size="16" /></LdsIconButton>
                  </LdsBox></td>}
                </tr>;
              })}</tbody>
            </LdsTable>
          </LdsBox>
          {filtered.length === 0 && <LdsTypography variant="bodyMd">Nenhuma policy encontrada.</LdsTypography>}
          <LdsBox className={styles.pagination}>
            <LdsBox className={styles.identity}>
              <LdsTypography variant="bodySm">{`${filtered.length} policies`}</LdsTypography>
              <LdsTypography variant="bodySm">{'Revis\u00e3o'}</LdsTypography>
              <LdsTypography variant="bodySm" className={styles.revision}>{draft.revision}</LdsTypography>
              {controller.dirty && <LdsChip label={'Altera\u00e7\u00f5es locais'} color="warning" size="sm" />}
            </LdsBox>
            <LdsBox className={styles.inline}>
              <LdsIconButton size="sm" ariaLabel="Pagina anterior" title="Pagina anterior" disabled={currentPage === 1}
                onClick={() => setPage(currentPage - 1)}><LdsIconChevronLeft size="16" /></LdsIconButton>
              <LdsTypography variant="bodySm">{`${currentPage} / ${lastPage}`}</LdsTypography>
              <LdsIconButton size="sm" ariaLabel="Proxima pagina" title="Proxima pagina" disabled={currentPage === lastPage}
                onClick={() => setPage(currentPage + 1)}><LdsIconChevronRight size="16" /></LdsIconButton>
            </LdsBox>
          </LdsBox>
        </LdsBox>

        {controller.validation && <LdsAlert color="warning" description={controller.validation} />}
        {controller.role === 'admin' && <LdsBox className={styles.footer}>
          <LdsButton variant="outlined" aria-label="Descartar alteracoes" disabled={!controller.canEdit || !controller.dirty}
            onClick={controller.discard}>{'Descartar altera\u00e7\u00f5es'}</LdsButton>
          <LdsButton variant="contained" aria-label="Salvar alteracoes" disabled={!controller.canSave}
            onClick={() => setConfirmation('save')}>{controller.saving ? 'Salvando...' : 'Salvar altera\u00e7\u00f5es'}</LdsButton>
        </LdsBox>}
      </>}

      {defaultMenu && <LdsMenu anchor={defaultMenu.anchor} className={styles.defaultMenu}
        ref={(node) => { node?.querySelector<HTMLElement>('[role="menuitem"]')?.focus({ preventScroll: true }); }}
        onBlur={(event: React.FocusEvent<HTMLElement>) => {
          if (!event.currentTarget.contains(event.relatedTarget)) setDefaultMenu(null);
        }}
        onKeyDown={(event: React.KeyboardEvent<HTMLElement>) => {
          if (event.key === 'Escape') closeDefaultMenu();
        }}>
        <LdsMenuItem onClick={() => selectDefault('public_default')} disabled={!controller.canEdit}
          selected={defaultMenu.value === draft?.public_default}>{'Usar como padr\u00e3o p\u00fablico'}</LdsMenuItem>
        <LdsMenuItem onClick={() => selectDefault('private_default')} disabled={!controller.canEdit}
          selected={defaultMenu.value === draft?.private_default}>{'Usar como padr\u00e3o privado'}</LdsMenuItem>
      </LdsMenu>}

      {controller.editor && <LdsDialog open title={controller.editor.index === null ? 'Adicionar policy' : 'Editar policy'}
        closeButtonAriaLabel="Fechar editor" onClose={controller.closeEditor} footer={<>
          <LdsButton variant="outlined" onClick={controller.closeEditor}>Cancelar</LdsButton>
          <LdsButton variant="contained" onClick={controller.applyEditor}>Aplicar ao rascunho</LdsButton>
        </>}>
        <LdsBox className={styles.editor}>
          {controller.editorError && <LdsAlert color="critical" description={controller.editorError} data-testid="alb-waf-editor-error" />}
          <LdsBox className={styles.field}><LdsFormField label="Valor da policy" fieldId="alb-policy-value">
            <LdsTextField id="alb-policy-value" aria-label="Valor da policy" value={controller.editor.entry.value} disabled={editorInUse}
              onChange={(event) => controller.changeEditor({ value: event.target.value })} />
          </LdsFormField></LdsBox>
          <LdsBox className={styles.field}><LdsFormField label={'Descri\u00e7\u00e3o'} fieldId="alb-policy-description">
            <LdsTextField id="alb-policy-description" aria-label="Descricao" value={controller.editor.entry.description}
              onChange={(event) => controller.changeEditor({ description: event.target.value })} />
          </LdsFormField></LdsBox>
          <LdsBox className={styles.inline}>
            <LdsCheckbox id="alb-policy-approved" aria-label="Policy aprovada" checked={controller.editor.entry.approved}
              disabled={editorInUse} onChange={(_event, { checked }) => controller.changeEditor({ approved: checked })} />
            <LdsTypography variant="bodyMd">Aprovada</LdsTypography>
          </LdsBox>
        </LdsBox>
      </LdsDialog>}

      {confirmation !== null && <LdsDialog open closeButtonAriaLabel="Fechar confirmacao"
        title={confirmation === 'save' ? 'Salvar configuracao' : confirmation === 'reload' ? 'Descartar rascunho' : 'Remover policy'}
        onClose={() => setConfirmation(null)} footer={<>
          <LdsButton variant="outlined" onClick={() => setConfirmation(null)}>Cancelar</LdsButton>
          <LdsButton variant="contained" aria-label={confirmation === 'save' ? 'Confirmar salvamento' : 'Confirmar'} onClick={confirm}>Confirmar</LdsButton>
        </>}>
        <LdsTypography variant="bodyMd">{confirmation === 'save'
          ? `Revisao ${draft?.revision}. ${draft?.catalog.length} policies no catalogo.`
          : confirmation === 'reload' ? 'As alteracoes locais serao descartadas.'
            : draft?.catalog[confirmation]?.value}</LdsTypography>
      </LdsDialog>}
    </LdsBox>
  );
}